import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Card, CardContent } from '../ui/card';
import { Target, Eye, Heart } from 'lucide-react';

const AboutSection = () => {
  const { language } = useLanguage();

  const content = {
    es: {
      title: "Misión, Visión y Valores",
      subtitle: "Nuestro compromiso con la excelencia",
      mission: {
        title: "Misión",
        text: "Proporcionar productos tecnológicos de calidad premium que transformen los espacios de nuestros clientes, ofreciendo soluciones innovadoras con el mejor servicio al cliente en República Dominicana."
      },
      vision: {
        title: "Visión",
        text: "Ser la empresa líder en comercialización de tecnología y electrodomésticos en República Dominicana, reconocida por nuestra excelencia, innovación y compromiso con la satisfacción del cliente."
      },
      values: {
        title: "Valores",
        items: [
          { label: "Excelencia", text: "Buscamos la perfección en cada producto y servicio que ofrecemos" },
          { label: "Confianza", text: "Construimos relaciones duraderas basadas en la transparencia y honestidad" },
          { label: "Innovación", text: "Nos mantenemos a la vanguardia de la tecnología" },
          { label: "Compromiso", text: "Dedicados a superar las expectativas de nuestros clientes" }
        ]
      }
    },
    en: {
      title: "Mission, Vision and Values",
      subtitle: "Our commitment to excellence",
      mission: {
        title: "Mission",
        text: "To provide premium quality technological products that transform our customers' spaces, offering innovative solutions with the best customer service in the Dominican Republic."
      },
      vision: {
        title: "Vision",
        text: "To be the leading company in the commercialization of technology and appliances in the Dominican Republic, recognized for our excellence, innovation and commitment to customer satisfaction."
      },
      values: {
        title: "Values",
        items: [
          { label: "Excellence", text: "We seek perfection in every product and service we offer" },
          { label: "Trust", text: "We build lasting relationships based on transparency and honesty" },
          { label: "Innovation", text: "We stay at the forefront of technology" },
          { label: "Commitment", text: "Dedicated to exceeding our customers' expectations" }
        ]
      }
    }
  };

  const t = content[language] || content.es;

  return (
    <section 
      id="about" 
      className="py-24 bg-muted/30"
      style={{
        scrollMarginTop: '80px' // Offset for fixed header
      }}
    >
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight">
            {t.title}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* Mission & Vision - Side by side on desktop */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {/* Mission */}
          <Card className="group hover:shadow-lg transition-all duration-300 border-none bg-background/50 backdrop-blur">
            <CardContent className="p-8 space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
                  <Target className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-2xl font-semibold">
                  {t.mission.title}
                </h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                {t.mission.text}
              </p>
            </CardContent>
          </Card>

          {/* Vision */}
          <Card className="group hover:shadow-lg transition-all duration-300 border-none bg-background/50 backdrop-blur">
            <CardContent className="p-8 space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors">
                  <Eye className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-2xl font-semibold">
                  {t.vision.title}
                </h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                {t.vision.text}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Values */}
        <Card className="border-none bg-background/50 backdrop-blur">
          <CardContent className="p-8 md:p-12">
            <div className="flex items-center gap-3 mb-8">
              <div className="p-3 rounded-full bg-primary/10">
                <Heart className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-2xl font-semibold">
                {t.values.title}
              </h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {t.values.items.map((value, index) => (
                <div 
                  key={index}
                  className="space-y-2 p-4 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <h4 className="font-semibold text-lg text-foreground">
                    {value.label}
                  </h4>
                  <p className="text-muted-foreground leading-relaxed text-sm">
                    {value.text}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default AboutSection;
